import React, { useEffect, useMemo, useState } from "react";
import { LISTING_CAR_GRID, SITE_CONTAINER } from "@/lib/siteLayout";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CarCard } from "./CarCard";
import { Reveal } from "@/hooks/useScrollReveal";
import { formatPrice, FUEL_TYPES } from "@/lib/format";
import { DRIVE_TYPES } from "@/lib/listingForm";
import {
  buildMakeCounts,
  countForMake,
  computePriceSliderBounds,
  priceSliderMillionTicks,
  PRICE_FILTER_STEP,
} from "@/lib/carCounts";
import { compareListingRank } from "@/lib/carBoost";
import { CountyFilter } from "@/components/CountyFilter";
import { SlidersHorizontal, X } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";

export const Inventory = ({
  cars,
  initialMake = "all",
  selectedCounties = [],
  onCountiesChange,
  onQuickView,
  compareList,
  onToggleCompare,
}) => {
  const { content } = useSiteContent();
  const inv = content.inventory || DEFAULT_SITE_CONTENT.inventory;
  const fl = inv.filter_labels || {};
  const sortOptions = inv.sort_options || DEFAULT_SITE_CONTENT.inventory.sort_options;
  const [make, setMake] = useState(initialMake !== "all" ? initialMake : "all");
  const [fuel, setFuel] = useState("all");
  const [drive, setDrive] = useState("all");
  const [year, setYear] = useState("all");
  const priceBounds = useMemo(() => computePriceSliderBounds(cars), [cars]);
  const [price, setPrice] = useState(() => [priceBounds.min, priceBounds.max]);
  const [sort, setSort] = useState("recommended");
  const [denseCards, setDenseCards] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1280px)");
    const sync = () => setDenseCards(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    setPrice((prev) => {
      const { min, max } = priceBounds;
      let lo = Math.min(prev[0], prev[1]);
      let hi = Math.max(prev[0], prev[1]);
      lo = Math.max(min, Math.min(lo, max));
      hi = Math.max(min, Math.min(hi, max));
      if (lo > hi) return [min, max];
      return [lo, hi];
    });
  }, [priceBounds.min, priceBounds.max]);

  const millionTicks = useMemo(
    () => priceSliderMillionTicks(priceBounds.min, priceBounds.max),
    [priceBounds.min, priceBounds.max],
  );

  useEffect(() => {
    if (!initialMake || initialMake === "all") return;
    setMake(initialMake);
    if (window.location.hash === "#keszlet") {
      requestAnimationFrame(() => {
        document.getElementById("keszlet")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [initialMake]);

  const makeCounts = useMemo(() => buildMakeCounts(cars), [cars]);
  const makes = useMemo(() => [...new Set(cars.map((c) => c.make))].sort(), [cars]);
  const years = useMemo(() => [...new Set(cars.map((c) => c.year))].sort((a, b) => b - a), [cars]);

  const filtered = useMemo(() => {
    let list = cars.filter((c) => {
      if (make !== "all" && c.make !== make) return false;
      if (fuel !== "all" && c.fuel !== fuel) return false;
      if (drive !== "all" && c.drive !== drive) return false;
      if (year !== "all" && String(c.year) !== year) return false;
      if (c.price < price[0] || c.price > price[1]) return false;
      if (selectedCounties.length > 0 && !selectedCounties.includes(c.county)) return false;
      return true;
    });
    const sorters = {
      recommended: (a, b) => compareListingRank(a, b),
      "price-asc": (a, b) => a.price - b.price,
      "price-desc": (a, b) => b.price - a.price,
      "year-desc": (a, b) => b.year - a.year,
      "power-desc": (a, b) => (b.horsepower || 0) - (a.horsepower || 0),
    };
    const secondary = sorters[sort] || sorters.recommended;
    return [...list].sort((a, b) => {
      const rankDiff = compareListingRank(a, b);
      if (rankDiff !== 0 && sort !== "recommended") return rankDiff;
      return secondary(a, b);
    });
  }, [cars, make, fuel, drive, year, price, sort, selectedCounties]);

  const activeMakeCount = make !== "all" ? countForMake(make, makeCounts) : filtered.length;

  const resetFilters = () => {
    setMake("all");
    setFuel("all");
    setDrive("all");
    setYear("all");
    setPrice([priceBounds.min, priceBounds.max]);
    onCountiesChange?.([]);
  };

  return (
    <section id="keszlet" data-testid="inventory-section" className="relative py-24">
      <div className={SITE_CONTAINER}>
        <Reveal>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-electric mb-3">
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} /> {inv.eyebrow}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
            {inv.title}
          </h2>
          <p className="text-slate-400 mt-3 max-w-xl">
            {inv.description}
            {make !== "all" && (
              <span className="block mt-2 text-electric font-medium" data-testid="inventory-make-count">
                {make}: {activeMakeCount} autó a weboldalon
              </span>
            )}
            {selectedCounties.length > 0 && (
              <span className="block mt-2 text-electric font-medium" data-testid="inventory-county-count">
                {selectedCounties.join(", ")}: {filtered.length} autó
              </span>
            )}
          </p>
        </Reveal>

        <Reveal className="mt-10">
          <div data-testid="filter-bar" className="glass rounded-2xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              <FilterSelect testid="filter-make" label={fl.make || "Márka"} value={make} onChange={setMake}
                options={[
                  { v: "all", l: `${inv.make_all_label || "Összes márka"} (${cars.length})` },
                  ...makes.map((m) => ({ v: m, l: `${m} (${makeCounts[m] || 0})` })),
                ]} />
              <FilterSelect testid="filter-fuel" label={fl.fuel || "Üzemanyag"} value={fuel} onChange={setFuel}
                options={[{ v: "all", l: inv.fuel_all_label || "Összes" }, ...FUEL_TYPES.map((f) => ({ v: f, l: f }))]} />
              <FilterSelect testid="filter-drive" label={fl.drive || "Hajtás"} value={drive} onChange={setDrive}
                options={[{ v: "all", l: "Összes" }, ...DRIVE_TYPES.map((d) => ({ v: d, l: d }))]} />
              <FilterSelect testid="filter-year" label={fl.year || "Évjárat"} value={year} onChange={setYear}
                options={[{ v: "all", l: inv.year_all_label || "Bármelyik" }, ...years.map((y) => ({ v: String(y), l: String(y) }))]} />
              <FilterSelect testid="filter-sort" label={fl.sort || "Rendezés"} value={sort} onChange={setSort}
                options={sortOptions.map((o) => ({ v: o.id, l: o.label }))} />
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{fl.area || "Terület"}</label>
                <CountyFilter cars={cars} selected={selectedCounties} onChange={onCountiesChange} />
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-[0.15em] uppercase text-slate-400">{fl.price || "Árkategória"}</span>
                <span className="text-sm text-electric font-medium">
                  {formatPrice(price[0])} – {formatPrice(price[1])}
                </span>
              </div>
              <div className="px-2.5" data-testid="filter-price-slider-wrap">
                <Slider
                  data-testid="filter-price-slider"
                  min={priceBounds.min}
                  max={priceBounds.max}
                  step={PRICE_FILTER_STEP}
                  value={price}
                  onValueChange={setPrice}
                  className="py-2"
                />
                {millionTicks.length > 0 && (
                  <div
                    className="relative mt-2 h-7 pointer-events-none select-none"
                    data-testid="filter-price-ticks"
                    aria-hidden
                  >
                    {millionTicks.map((tick) => {
                      const span = priceBounds.max - priceBounds.min;
                      const left = span > 0 ? ((tick - priceBounds.min) / span) * 100 : 0;
                      const label =
                        tick >= 1_000_000 ? `${tick / 1_000_000}M` : `${tick / 1000}e`;
                      return (
                        <div
                          key={tick}
                          className="absolute top-0 flex flex-col items-center -translate-x-1/2"
                          style={{ left: `${left}%` }}
                        >
                          <div className="w-px h-2 bg-white/30" />
                          <span className="text-[10px] text-slate-500 mt-0.5 leading-none">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <span data-testid="results-count" className="text-sm text-slate-400">
                {(inv.results_count_label || "{count} autó található").replace("{count}", String(filtered.length))}
              </span>
              <button
                data-testid="reset-filters"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" /> {inv.reset_filters_label || "Szűrők törlése"}
              </button>
            </div>
          </div>
        </Reveal>

        <div className={`${LISTING_CAR_GRID} mt-10`}>
          {filtered.map((car, i) => (
            <Reveal key={car.id} delay={(i % 5) * 80}>
              <CarCard
                car={car}
                compact={denseCards}
                onQuickView={onQuickView}
                selected={compareList.some((c) => c.id === car.id)}
                onToggleCompare={onToggleCompare}
              />
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <div data-testid="no-results" className="text-center py-20 text-slate-400">
            {inv.empty_message}
          </div>
        )}
      </div>
    </section>
  );
};

const FilterSelect = ({ testid, label, value, onChange, options }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white/[0.03] border-white/10 text-white h-11">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-anthracite-700 border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o.v} value={o.v} className="focus:bg-electric focus:text-white">
            {o.l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
